import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterAltOff as FilterOffIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../config/api';
import { toast } from 'react-toastify';
import { useApiError } from '../hooks/useApiError';

const STATUS_CORES = {
  pendente: 'warning',
  processando: 'info',
  enviada: 'success',
  falhou: 'error',
  cancelada: 'default'
};

const STATUS_OPTIONS = ['pendente', 'processando', 'enviada', 'falhou', 'cancelada'];

const filtrosVazios = {
  idInstancia: '',
  status: '',
  destinatario: '',
  busca: '',
  data_de: '',
  data_ate: '',
  idFila: ''
};

function Mensagens() {
  const [instancias, setInstancias] = useState([]);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filtros, setFiltros] = useState(filtrosVazios);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const { handleError } = useApiError();

  const fetchEstatisticas = useCallback(async () => {
    try {
      const r = await api.get('/instancias/fila/todas/estatisticas');
      setEstatisticas(r.data);
    } catch (err) {
      // Não bloqueia a página se estatísticas falharem
      console.warn('Erro estatísticas:', err);
    }
  }, []);

  const fetchInstancias = useCallback(async () => {
    try {
      const r = await api.get('/instancias');
      setInstancias(r.data || []);
    } catch (err) {
      handleError(err, 'Erro ao listar instâncias');
    }
  }, [handleError]);

  const fetchMensagens = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limite: rowsPerPage, offset: page * rowsPerPage };
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== '' && v != null) params[k] = v;
      });
      const r = await api.get('/instancias/fila/todas', { params });
      setItens(r.data.itens || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      handleError(err, 'Erro ao listar mensagens');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filtros, handleError]);

  useEffect(() => { fetchInstancias(); }, [fetchInstancias]);
  useEffect(() => { fetchMensagens(); fetchEstatisticas(); }, [fetchMensagens, fetchEstatisticas]);

  // Auto-refresh a cada 5s quando ativado.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchMensagens();
      fetchEstatisticas();
    }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchMensagens, fetchEstatisticas]);

  const handleAplicarFiltros = (e) => {
    if (e) e.preventDefault();
    setPage(0);
    fetchMensagens();
  };

  const handleLimparFiltros = () => {
    setFiltros(filtrosVazios);
    setPage(0);
  };

  const handleCancelar = async (item) => {
    if (!window.confirm(`Cancelar mensagem #${item.idFila}?`)) return;
    try {
      const inst = instancias.find(i => i.idInstancia === item.idInstancia);
      const headers = inst?.token ? { Authorization: `Bearer ${inst.token}` } : undefined;
      await api.delete(`/instancias/${item.idInstancia}/fila/${item.idFila}`, { headers });
      toast.success('Mensagem cancelada');
      fetchMensagens();
      fetchEstatisticas();
    } catch (err) {
      handleError(err, 'Erro ao cancelar');
    }
  };

  const formatarData = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('pt-BR'); }
    catch { return d; }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Mensagens</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={autoRefresh ? 'contained' : 'outlined'}
            color={autoRefresh ? 'primary' : 'inherit'}
            onClick={() => setAutoRefresh(v => !v)}
          >
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <IconButton onClick={() => { fetchMensagens(); fetchEstatisticas(); }} title="Atualizar">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Cards de estatísticas */}
      {estatisticas && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {['total', 'pendente', 'processando', 'enviada', 'falhou', 'cancelada'].map(k => (
            <Grid item xs={6} sm={4} md={2} key={k}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {k}
                </Typography>
                <Typography variant="h5">{estatisticas[k] ?? 0}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filtros */}
      <Paper component="form" onSubmit={handleAplicarFiltros} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Instância</InputLabel>
              <Select
                label="Instância"
                value={filtros.idInstancia}
                onChange={(e) => setFiltros(f => ({ ...f, idInstancia: e.target.value }))}
              >
                <MenuItem value=""><em>Todas</em></MenuItem>
                {instancias.map(i => (
                  <MenuItem key={i.idInstancia} value={i.idInstancia}>
                    {i.nome} {i.numero_whatsapp ? `(${i.numero_whatsapp})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filtros.status}
                onChange={(e) => setFiltros(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value=""><em>Todos</em></MenuItem>
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small"
              label="Destinatário"
              placeholder="55119..."
              value={filtros.destinatario}
              onChange={(e) => setFiltros(f => ({ ...f, destinatario: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small"
              label="Buscar (mensagem ou erro)"
              value={filtros.busca}
              onChange={(e) => setFiltros(f => ({ ...f, busca: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small"
              label="ID Fila"
              type="number"
              value={filtros.idFila}
              onChange={(e) => setFiltros(f => ({ ...f, idFila: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small"
              label="De"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={filtros.data_de}
              onChange={(e) => setFiltros(f => ({ ...f, data_de: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small"
              label="Até"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={filtros.data_ate}
              onChange={(e) => setFiltros(f => ({ ...f, data_ate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button type="submit" variant="contained" size="small">Filtrar</Button>
            <Button onClick={handleLimparFiltros} startIcon={<FilterOffIcon />} size="small">
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Instância</TableCell>
                    <TableCell>Destinatário</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tentativas</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell>Enviado em</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        Nenhuma mensagem encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    itens.map(item => (
                      <TableRow key={item.idFila} hover>
                        <TableCell>{item.idFila}</TableCell>
                        <TableCell>
                          <Tooltip title={item.idInstancia} arrow>
                            <span>{item.nome_instancia || item.idInstancia.slice(0, 8)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{item.destinatario}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={item.mensagem || ''} arrow>
                            <span>{item.mensagem || '-'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{item.tipo}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.status} color={STATUS_CORES[item.status] || 'default'} />
                        </TableCell>
                        <TableCell>{item.tentativas}/{item.max_tentativas}</TableCell>
                        <TableCell>{formatarData(item.data_criacao)}</TableCell>
                        <TableCell>{formatarData(item.data_envio)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setDetalhe(item)} title="Ver detalhes">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          {(item.status === 'pendente' || item.status === 'processando') && (
                            <IconButton size="small" onClick={() => handleCancelar(item)} title="Cancelar">
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 250]}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Dialog de detalhes */}
      <Dialog open={!!detalhe} onClose={() => setDetalhe(null)} maxWidth="md" fullWidth>
        <DialogTitle>Mensagem #{detalhe?.idFila}</DialogTitle>
        <DialogContent>
          {detalhe && (
            <Box sx={{ '& .row': { display: 'flex', gap: 2, mb: 1 }, '& .label': { minWidth: 140, color: 'text.secondary' } }}>
              <div className="row"><span className="label">Status:</span>
                <Chip size="small" label={detalhe.status} color={STATUS_CORES[detalhe.status] || 'default'} />
              </div>
              <div className="row"><span className="label">Instância:</span>
                <span>{detalhe.nome_instancia || '-'} <code style={{ color: '#888' }}>({detalhe.idInstancia})</code></span>
              </div>
              <div className="row"><span className="label">Destinatário:</span><span>{detalhe.destinatario}</span></div>
              <div className="row"><span className="label">Tipo:</span><span>{detalhe.tipo}</span></div>
              <div className="row"><span className="label">Prioridade:</span><span>{detalhe.prioridade}</span></div>
              <div className="row"><span className="label">Tentativas:</span><span>{detalhe.tentativas} de {detalhe.max_tentativas}</span></div>
              <div className="row"><span className="label">Criado em:</span><span>{formatarData(detalhe.data_criacao)}</span></div>
              <div className="row"><span className="label">Processado em:</span><span>{formatarData(detalhe.data_processamento)}</span></div>
              <div className="row"><span className="label">Enviado em:</span><span>{formatarData(detalhe.data_envio)}</span></div>
              <div className="row"><span className="label">Agendado para:</span><span>{formatarData(detalhe.agendado_para)}</span></div>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Conteúdo</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
                {detalhe.mensagem || <em>(sem texto)</em>}
              </Paper>

              {detalhe.url_imagem && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>URL da imagem</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <a href={detalhe.url_imagem} target="_blank" rel="noreferrer">{detalhe.url_imagem}</a>
                  </Typography>
                </>
              )}

              {detalhe.erro && (
                <>
                  <Typography variant="subtitle2" color="error" sx={{ mt: 2 }}>Erro</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(255,0,0,0.04)', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {detalhe.erro}
                  </Paper>
                </>
              )}

              {detalhe.resposta && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Resposta do envio</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: 200 }}>
                    {typeof detalhe.resposta === 'string'
                      ? detalhe.resposta
                      : JSON.stringify(detalhe.resposta, null, 2)}
                  </Paper>
                </>
              )}

              {detalhe.metadados && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Metadados</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {typeof detalhe.metadados === 'string'
                      ? detalhe.metadados
                      : JSON.stringify(detalhe.metadados, null, 2)}
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(detalhe?.status === 'pendente' || detalhe?.status === 'processando') && (
            <Button color="error" onClick={() => { handleCancelar(detalhe); setDetalhe(null); }}>
              Cancelar mensagem
            </Button>
          )}
          <Button onClick={() => setDetalhe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Mensagens;
